import useSWR from 'swr'
import type { Locale } from '@/i18n/config'
import { useLocale } from 'next-intl'
import type { ITranslatedTextModel } from '@/lib/db/types'
import { useCallback, useRef } from 'react'

type ITranslationsResponse = {
  success: boolean
  translatedText: ITranslatedTextModel[]
}

type ITranslationResponse = {
  success: boolean
  i18n: Record<string, string>
}

const fetcher = (url: string) => fetch(url).then((res) => res.json())

// Cache duration in milliseconds 如果 20 秒对同一个 text 发送过请求，就不要再发送了
const CACHE_DURATION = 20 * 1000

export function useDynamicI18n({
  previewMode = false,
}: {
  previewMode?: boolean
} = {}) {
  const locale = useLocale()
  const requestCache = useRef<Record<string, number>>({})

  const { data, mutate, isLoading, error } = useSWR<ITranslationsResponse>(
    '/api/dynamic-i18n',
    fetcher,
    {
      revalidateOnFocus: false,
    },
  )

  const translations = data?.translatedText || []
  const isInitialLoading = isLoading && !data && !error

  // 异步获取翻译，不等待结果
  const fetchTranslation = useCallback(
    async (key: string) => {
      const now = Date.now()
      const lastRequestTime = requestCache.current[key]
      // If the last request was made within the cache duration, skip the request
      if (lastRequestTime && now - lastRequestTime < CACHE_DURATION) {
        return
      }
      // Update the cache with the current timestamp
      requestCache.current[key] = now
      try {
        const response = await fetch('/api/dynamic-i18n', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text: key }),
        })
        const data: ITranslationResponse = await response.json()

        if (data.success) {
          // 重新获取所有翻译以更新缓存
          mutate()
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      }
    },
    [mutate],
  )

  const dt = useCallback(
    (key: string, store: boolean = true): string => {
      if (previewMode) {
        return key
      }

      if (isInitialLoading) {
        return key
      }
      // 检查已加载的翻译
      const translation = translations.find((item) => item.text === key)
      if (translation && Object.keys(translation.i18n).length > 0) {
        return translation.i18n[locale as Locale] || key
      }
      console.log('translation not found or i18n is empty: ', key)
      // 只有在初始数据加载完成后且 store 为 true 时才触发翻译请求
      if (store) {
        fetchTranslation(key)
      }
      // 直接返回原始键
      return key
    },
    [translations, locale, fetchTranslation, previewMode, isInitialLoading],
  )

  return {
    dt,
    isLoading: isInitialLoading,
  }
}
